import React, { useState } from "react";
import {
  Button,
  Menu,
  MenuItem,
  CircularProgress,
  Box,
  IconButton,
  Tooltip,
  Typography,
  Divider,
  Icon,
  Modal,
} from "@mui/material";
import { application } from "globals/endpoints";
import MDTypography from "components/MDTypography";
import CTLNotification from "../CTLNotification";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import SyncIcon from "@mui/icons-material/Sync";
import BackupIcon from "@mui/icons-material/Backup";
import { ListItemIcon } from "@mui/material";
import MDButton from "components/MDButton";


const CTLNodeSync = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [mode, setMode] = useState(null); // bookmark | commit | apply
  const [isLoading, setIsLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("");

  const [configName, setConfigName] = useState("");
  const [syncEnabled, setSyncEnabled] = useState(false);

  const [fileList, setFileList] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [deleteConfirmAnchorEl, setDeleteConfirmAnchorEl] = useState(null);

  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
    pagename: "Sync",
    status: "",
  });

  const open = Boolean(anchorEl);
  const deleteConfirmOpen = Boolean(deleteConfirmAnchorEl);

  /* ---------------- MENU ---------------- */
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setDeleteConfirmAnchorEl(null);
    setMode(null);
    setSelectedFile(null);
    setConfigName("");
    setSyncEnabled(false);
  };

  /* ---------------- LOAD FILES ---------------- */
  const loadFiles = () => {
    application
      .get("/node-sync/list")
      .then((res) => {
        setFileList(res?.data?.data?.data || []);
      })
      .catch(() => setFileList([]))
  };

  /* ---------------- COMMIT / BOOKMARK ---------------- */
  const handleStartCommit = (isSync) => {
    setLoadingText(
      isSync ? "Commit in progress..." : "Bookmark in progress..."
    );
    setIsLoading(true);

    application
      .get("/node-sync/commit", {
        params: {
          configName: configName,
          syncNow: isSync,
        },
      })
      .then((res) => {
        setNotify({
          isOpen: true,
          message: res?.data?.message,
          type: "success",
          pagename: "Sync",
          status: isSync
            ? "Commit Completed Successfully"
            : "Bookmark Completed Successfully",
        });
      })
      .catch((error) => {
        setNotify({
          isOpen: true,
          message: "Failed to commit",
          type: "error",
          pagename: "Sync",
          status: error?.message,
        });
      })
      .finally(() => {
        setIsLoading(false);
        setLoadingText("");
      });

    handleClose();
  };


  const handleApply = () => {
    if (!selectedFile) {
      setNotify({
        isOpen: true,
        message: "",
        type: "warning",
        pagename: "Sync",
        status: "No file selected for apply",
      });
      return;
    }

    setLoadingText("Applying backup...");
    setIsLoading(true);

    application
      .get("/node-sync/apply", {
        params: {
          inputPath: selectedFile?.metadata?.configId,
        },
      })
      .then((res) => {
        setNotify({
          isOpen: true,
          status: res?.data?.message,
          type: "success",
          pagename: "Sync",
        });
      })
      .catch((error) => {
        setNotify({
          isOpen: true,
          message: "Failed to start apply",
          type: "error",
          pagename: "Sync",
          status: error?.message,
        });
      })
      .finally(() => {
        setIsLoading(false);
        setLoadingText("");
      });

    handleClose();
  };

  /* ---------------- DELETE ---------------- */
  const handleDelete = () => {
    if (!selectedFile) {
      setNotify({
        isOpen: true,
        message: "",
        type: "warning",
        pagename: "Sync",
        status: "No file selected",
      });
      return;
    }

    setLoadingText("Deleting backup...");
    setIsLoading(true);

    application
      .post("/node-sync/delete", null, {
        params: {
          filePath: selectedFile.filePath,
        },
      })
      .then((res) => {
        setNotify({
          isOpen: true,
          message: "Backup file deleted",
          type: "success",
          pagename: "Sync",
          status: res?.data?.message,
        });
        loadFiles();
        setSelectedFile(null);
      })
      .catch((error) => {
        setNotify({
          isOpen: true,
          message: "Delete failed",
          type: "error",
          pagename: "Sync",
          status: error?.message,
        });
      })
      .finally(() => {
        setIsLoading(false);
        setLoadingText("");
        setDeleteConfirmAnchorEl(null);
      });
  };

  const handleOpenDeleteConfirm = (event) => {
    if (!selectedFile) {
      return;
    }
    setDeleteConfirmAnchorEl(event.currentTarget);
  };

  const handleCloseDeleteConfirm = () => {
    setDeleteConfirmAnchorEl(null);
  };

  return (
    <Box sx={{ p: 1 }}>
      <Tooltip title="Programming Sync" arrow>
        <IconButton
          onClick={handleMenuOpen}
          size="large"
          disableRipple
          sx={({
            palette: { info, primary, common },
            functions: { rgba },
          }) => ({
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: common.white,
            borderRadius: "50%",
            width: 50,
            height: 50,
            p: 0,
            alignItems: "center",
            justifyContent: "center",
            mr: 0,
            ml: 0,
            transition: "all 0.3s ease",
            "&:hover": {
              background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
              boxShadow: `0 8px 20px ${rgba(info.main, 0.38)}`,
              transform: "scale(1.08)",
            },
            "&:active": {
              transform: "scale(0.95)",
            },
          })}
        >
          <SyncIcon
            sx={{
              fontSize: "30px",
              animation: "syncSpin 12s linear infinite",
              "@keyframes syncSpin": {
                "0%": {
                  transform: "rotate(0deg)",
                },
                "100%": {
                  transform: "rotate(360deg)",
                },
              },
            }}
          />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: "left", vertical: "top" }}
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
        PaperProps={{
          sx: ({
            palette: { background, light },
            borders: { borderWidth },
            functions: { rgba },
          }) => ({
            mt: 1,
            borderRadius: "12px",
            border: `${borderWidth[1]} solid ${light.main}`,
            // minWidth: 280,
            backgroundColor: background.paper,
            boxShadow: `0 16px 30px ${rgba(light.main, 0.55)}`,
            p: 0.75,
          }),
        }}
      >
        {/* MAIN MENU */}
        {!mode && (
          <Box py={0.5} display="flex" flexDirection="column" gap={0.5}>
            {[
              {
                label: "Bookmark",
                mode: "bookmark",
                icon: <BookmarkIcon color="primary" fontSize="small" />,
                action: () => handleStartCommit(false),
              },
              {
                label: "Commit",
                mode: "commit",
                icon: <SyncIcon color="info" fontSize="small" />,
                action: () => handleStartCommit(true),
              },
              {
                label: "Load Backup",
                mode: "apply",
                icon: <BackupIcon color="success" fontSize="small" />,
                action: () => {
                  setMode("apply");
                  loadFiles();
                },
              },
            ].map((item) => (
              <MenuItem
                key={item.mode}
                onClick={() => {
                  setMode(item.mode);
                  item.action();
                }}
                sx={({ palette: { info }, functions: { rgba } }) => ({
                  borderRadius: "10px",
                  mb: 0.5,
                  gap: 1,
                  py: 1,
                  px: 1.1,
                  transition: "all 160ms ease",
                  "&:hover": {
                    transform: "translateX(2px)",
                    backgroundColor: rgba(info.main, 0.08),
                  },
                })}
              >
                <ListItemIcon sx={{ minWidth: 25 }}>{item.icon}</ListItemIcon>

                <Typography fontSize="14px" fontWeight={500}>
                  {item.label}
                </Typography>
              </MenuItem>
            ))}
          </Box>
        )}

        {/* APPLY SCREEN */}
        {mode === "apply" && (
          <Box p={1.2} >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography fontWeight="medium" color="text">
                Select Backup File
              </MDTypography>

              {selectedFile && (
                <Tooltip title="Select backup delete" arrow>
                  <IconButton
                    size="small"
                    onClick={handleOpenDeleteConfirm}
                    sx={({ palette: { error }, functions: { rgba } }) => ({
                      borderRadius: "8px",
                      backgroundColor: rgba(error.main, 0.08),
                    })}
                  >
                    <Icon color="error">delete</Icon>
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box
              sx={({
                palette: { light, info },
                borders: { borderWidth },
                functions: { rgba },
              }) => ({
                maxHeight: 250,
                overflowY: "auto",
                border: `${borderWidth[1]} solid ${light.main}`,
                borderRadius: "10px",
                px: 0.5,
                py: 0.5,
                scrollbarWidth: "thin",
                scrollbarColor: `${rgba(info.main, 0.4)} ${rgba(light.main, 0.6)}`,
                "&::-webkit-scrollbar": {
                  width: "5px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: rgba(info.main, 0.35),
                  borderRadius: "6px",
                },
              })}
            >
              {fileList.map((item) => (
                <MenuItem
                  key={item.fileName}
                  selected={selectedFile?.fileName === item.fileName}
                  onClick={() => setSelectedFile(item)}
                  sx={({ palette: { info }, functions: { rgba } }) => ({
                    borderRadius: "8px",
                    my: 0.2,
                    py: 1,
                    px: 1,
                    transition: "all 150ms ease",
                    "&.Mui-selected": {
                      backgroundColor: rgba(info.main, 0.12),
                    },
                    "&.Mui-selected:hover": {
                      backgroundColor: rgba(info.main, 0.16),
                    },
                  })}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {item.metadata.configName}
                      <MDTypography
                        component="span"
                        variant="caption"
                        sx={{ ml: 1.5 }}
                      >
                        {(item.sizeBytes / (1024 * 1024)).toFixed(2)} MB
                      </MDTypography>
                    </Typography>

                    <MDTypography variant="caption">
                      Last Modified: {item.lastModified}
                    </MDTypography>
                  </Box>
                </MenuItem>
              ))}

              {fileList.length === 0 && (
                <Box textAlign="center" p={2}>
                  <MDTypography>No files found</MDTypography>
                </Box>
              )}
            </Box>

            <Box mt={2} display="flex" gap={1} justifyContent="flex-end">
              <MDButton
                variant="contained"
                color="secondary"
                size="small"
                onClick={handleClose}
                sx={{ textTransform: "none", borderRadius: "8px", px: 2 }}
              >
                Cancel
              </MDButton>

              <MDButton
                variant="contained"
                color="info"
                size="small"
                onClick={handleApply}
                sx={{ textTransform: "none", borderRadius: "8px", px: 2 }}
              >
                Apply
              </MDButton>
            </Box>
          </Box>
        )}
      </Menu>

      <Menu
        anchorEl={deleteConfirmAnchorEl}
        open={deleteConfirmOpen}
        onClose={handleCloseDeleteConfirm}
        PaperProps={{
          sx: ({ palette: { background, light }, borders: { borderWidth } }) => ({
            borderRadius: "12px",
            width: 240,
            maxWidth: 240,
            border: `${borderWidth[1]} solid ${light.main}`,
            backgroundColor: background.paper,
            p: 1.2,
          }),
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Box>
          <MDTypography
            variant="button"
            color="text"
            fontWeight="medium"
            sx={{
              display: "block",
              whiteSpace: "normal",
              lineHeight: 1.45,
            }}
          >
            Are you sure you want to delete the selected backup file?
          </MDTypography>
          <Box mt={1.5} display="flex" justifyContent="flex-end" gap={1}>
            <MDButton
              variant="contained"
              color="secondary"
              size="small"
              onClick={handleCloseDeleteConfirm}
              sx={{ textTransform: "none", borderRadius: "8px" }}
            >
              Cancel
            </MDButton>
            <MDButton
              variant="contained"
              color="error"
              size="small"
              onClick={handleDelete}
              sx={{ textTransform: "none", borderRadius: "8px" }}
            >
              Delete
            </MDButton>
          </Box>
        </Box>
      </Menu>

      <Modal
        open={isLoading}
        disableAutoFocus
        disableEscapeKeyDown
        keepMounted
      >
        <Box
          sx={({ palette: { common }, functions: { rgba } }) => ({
            position: "fixed",
            inset: 0,
            width: "100vw",
            height: "100dvh",
            zIndex: (theme) => theme.zIndex.modal + 99999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(2px)",
          })}
        >
          <Box
            sx={({ palette: { background, common, light }, borders: { borderWidth } }) => ({
              minWidth: 260,
              maxWidth: 340,
              px: 3,
              py: 2.5,
              borderRadius: "14px",
              border: `${borderWidth[1]} solid ${light.main}`,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.4,
            })}
          >
            <CircularProgress color="info" size={46} />
            <MDTypography fontSize="18px" fontWeight={500} color="text">
              {loadingText || "Please wait..."}
            </MDTypography>
          </Box>
        </Box>
      </Modal>

      <CTLNotification notify={notify} setNotify={setNotify} />
    </Box>
  );
};

export default CTLNodeSync;
